import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useState, useMemo, useCallback } from 'react';
import { useDisclosure } from '@chakra-ui/react';
import { useDataGroup, useChatbot, useEditTaskData } from '@/lib/sdk/chatbot/queries';
import { useAuth } from '@/lib/sdk/auth/hooks';
import { useToast } from '@/client/hooks/useToast';
import { helpers } from '@/client/utils/helpers';
import { isHiddenTaskType } from '@/client/utils/taskTypeMapping';
import type { TaskData } from '@/lib/sdk/chatbot/types';

export function useTaskGroupDataPage() {
  const { botId, orgId, groupId } = useParams<{ botId: string; orgId: string; groupId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const toast = useToast();
  const { user, currentOrg, userPermission, featurePermissions } = useAuth();

  const userMobile = (user?.country_code || '') + (user?.phone_number || '');
  const authOrgId = currentOrg?.id || '';

  const [selectedTask, setSelectedTask] = useState<TaskData | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const { data: taskGroup, isLoading: tasksLoading, refetch } = useDataGroup(botId!, groupId!);
  const { data: chatbot, isLoading: chatbotsLoading } = useChatbot(botId!);
  const editTaskData = useEditTaskData();

  const isSuperAdmin = user?.is_super_admin === true;
  const hasEditPermission =
    helpers.hasPermission(userPermission || 'read', 'read_write') ||
    isSuperAdmin;
  // The per-bot data_editing_enabled toggle is deprecated (#721) — edit_data/
  // add_data are the sole gate now, narrowing canEdit below. The server
  // remains authoritative; this only avoids offering an action the request
  // would be rejected for.
  const canEdit = hasEditPermission;
  const canAddData = canEdit && helpers.hasFeatureAction(featurePermissions, userPermission, isSuperAdmin, 'workflows', 'add_data');
  const canEditData = canEdit && helpers.hasFeatureAction(featurePermissions, userPermission, isSuperAdmin, 'workflows', 'edit_data');
  const isLoading = tasksLoading || chatbotsLoading;

  const tasks = useMemo(() => {
    if (!taskGroup?.items) return [];
    return taskGroup.items.filter((task) => !isHiddenTaskType(task.type));
  }, [taskGroup]);

  const completedTasks = tasks.filter((t) => t.rep_status == 1).length;
  const totalTasks = tasks.length;
  const progress = totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0;

  const handleOpenTask = useCallback((task: TaskData) => {
    setSelectedTask(task);
    onOpen();
  }, [onOpen]);

  const handleCloseModal = useCallback(() => {
    setSelectedTask(null);
    onClose();
    refetch();
  }, [onClose, refetch]);

  const handleSubmit = useCallback(async (taskId: string, noteId: string, value: string, repId: number) => {
    if (!userMobile || !authOrgId) { toast.error('User authentication required'); return; }
    try {
      await editTaskData.mutateAsync({
        userId: userMobile, orgId: authOrgId,
        botCode: chatbot?.short_code || botId!,
        taskId, repId, noteId: noteId || '_new', newValue: value,
      });
      toast.success('Task updated successfully');
      await refetch();
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : 'Failed to update task');
      throw error;
    }
  }, [botId, chatbot, editTaskData, toast, refetch, userMobile, authOrgId]);

  const handleBack = useCallback(() => {
    const queryString = searchParams.toString();
    navigate(`/dashboard/${orgId}/workflows/${botId}/data${queryString ? `?${queryString}` : ''}`);
  }, [navigate, orgId, botId, searchParams]);

  const queryString = searchParams.toString();
  const breadcrumbs = [
    { label: 'Workflows', href: `/dashboard/${orgId}/workflows` },
    { label: 'Data', href: `/dashboard/${orgId}/workflows/${botId}/data${queryString ? `?${queryString}` : ''}` },
    { label: 'Edit Data Group' },
  ];

  return {
    botId, orgId,
    isLoading, taskGroup, tasks, chatbot,
    hasEditPermission, canEdit, canAddData, canEditData,
    completedTasks, totalTasks, progress,
    selectedTask,
    isOpen, handleCloseModal,
    handleOpenTask, handleSubmit, handleBack,
    isSubmitting: editTaskData.isPending,
    breadcrumbs,
  };
}
